import React, { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { WAYPOINT_COLOR, WaypointEdge } from "../../../../interfaces/robot/rooms/waypoints";
import ZoneControllerMouseState from "../../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { MapHistoryAction, MapHistoryType } from "../../../../interfaces/robot/zone-planner/map-history";
import { selectSelectedRoomWaypoints } from "../../../../store/room/room-selectors";

const useStyles = makeStyles()((theme) => ({
  edgeLineDash: {
    stroke: WAYPOINT_COLOR,
    strokeOpacity: 0.7,
    strokeDasharray: "0 10 0",
    pointerEvents: "none",
  },
  edgeLineOuter: {
    strokeLinejoin: "round",
    stroke: theme.palette.grey[900],
    strokeOpacity: 0.3,
    pointerEvents: "visible",
  },
  edgeLineHover: {
    "&:hover": {
      strokeOpacity: 0.5,
    },
  },
  notSelectable: {
    pointerEvents: "none",
    opacity: 0.4,
  },
}));

const WayPointEdge: React.FC<{
  edge?: WaypointEdge;
  svgPoints: ({ x: number; y: number } | null)[];
  mouseState: ZoneControllerMouseState;
  deletable?: boolean;
  notSelectable?: boolean;
}> = ({ edge, svgPoints, mouseState, deletable = true, notSelectable = false }) => {
  const { classes, cx } = useStyles();

  const dispatch = useSharedDispatch();

  const { upsertWaypointSet, addUndoableAction } = useSharedActions();

  const waypointsData = useSharedSelector((state) => selectSelectedRoomWaypoints(state));

  const edgeClickHandler = () => {
    if (mouseState === ZoneControllerMouseState.DeleteZone && edge !== undefined && deletable) {
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Patched,
          type: MapHistoryType.WaypointSet,
          changedData: {
            ...waypointsData,
          },
        })
      );

      const remainingEdges = waypointsData.edges.filter(e => e.id !== edge.id);
      const connectedNodeIds = new Set<string>();
      for (const e of remainingEdges) {
        connectedNodeIds.add(e.u);
        connectedNodeIds.add(e.v);
      }
      const remainingNodes = waypointsData.nodes.filter(n => connectedNodeIds.has(n.id));

      dispatch(upsertWaypointSet({ room: edge.room, nodes: remainingNodes, edges: remainingEdges }));
    }
  };

  return (
    <React.Fragment>
      <polygon
        points={svgPoints
          .filter((point) => point != null)
          .map((point) => `${point!.x},${point!.y}`)
          .join(" ")}
        className={cx(classes.edgeLineOuter, { [classes.edgeLineHover]: deletable, [classes.notSelectable]: notSelectable })}
        strokeWidth={12}
        onClick={edgeClickHandler}
        style={{ pointerEvents: !deletable ? "none" : undefined }}
      />
      <line
        x1={svgPoints[0]!.x}
        y1={svgPoints[0]!.y}
        x2={svgPoints[1]!.x}
        y2={svgPoints[1]!.y}
        className={cx(classes.edgeLineDash, {[classes.notSelectable]: notSelectable})}
        strokeWidth={1}
      />
    </React.Fragment>
  );
};

export default WayPointEdge;
